// google-docs.service.ts
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

declare const google: any;
declare const gapi: any;

export interface DriveDoc {
  id: string;
  name: string;
  modifiedTime?: string;
  owner?: string;
  url?: string;
}

interface GoogleDocsConfig {
  clientId: string;
  googleDeveloperKey: string;

  /**
   * Legacy AI+tools endpoint (kept for list/search).
   * If you still use your old AWS lambda URL for list/search, set it in env.
   */
  lambdaUrl: string;

  /**
   * NEW: Base URL for Google Cloud Functions.
   * We will call `${cloudFunctionsBaseUrl}/generateGemini` for doc creation.
   */
  cloudFunctionsBaseUrl: string;

  /**
   * Optional default folder ID to create docs into.
   */
  defaultDocFolderId?: string;
}

interface LambdaResponse {
  text?: string;
  model?: string;
  length?: string;
  usage?: any;
  tool_calls?: Array<{
    name?: string;
    args?: any;
    result?: any;
  }>;
  error?: string;
  requestId?: string;
  new_access_token?: string;
}

interface GenerateGeminiResponse {
  success?: boolean;
  google_doc?: {
    success?: boolean;
    document_id?: string;
    documentId?: string;
    title?: string;
    url?: string;
    message?: string;
    error?: string;
    status?: number;
  };
  new_access_token?: string;
  error?: string;
  details?: any;
}

const DEFAULT_GOOGLE_DOCS_SERVICE_URL =
  'https://v2quhhx6duwsv3i4m7gcbrlafm0paopu.lambda-url.us-east-1.on.aws/';

// ✅ LIVE BASE URL (hardcoded as requested)
const CLOUD_FUNCTIONS_BASE_URL = 'https://us-central1-clarichatai.cloudfunctions.net/';

@Injectable({
  providedIn: 'root'
})
export class GoogleDocsService {
  private readonly scopes = [
    'https://www.googleapis.com/auth/drive.file',
    'https://www.googleapis.com/auth/drive.readonly',
    'https://www.googleapis.com/auth/documents',
    'https://www.googleapis.com/auth/userinfo.email'
  ].join(' ');

  private tokenClient?: any;
  private accessToken: string | null = null;
  private tokenExpiryMs = 0;

  private pickerReady = false;
  private gapiReady = false;
  private initPromise?: Promise<void>;

  private userEmail?: string;

  private lastCreateAt = 0;
  private readonly createCooldownMs = 5000;

  private readonly config: GoogleDocsConfig = {
    clientId: this.readConfigValue([
      'oauth2ClientId',
      'googleDriveOAuthClientId',
      'OAUTH2_CLIENT_ID'
    ]),
    googleDeveloperKey: this.readConfigValue(['googleApiKey', 'GOOGLE_API_KEY', 'GAPI_KEY']),

    // Legacy list/search endpoint (optional to keep)
    lambdaUrl:
      this.readConfigValue(['googleDocsServiceUrl', 'GOOGLE_DOCS_SERVICE_URL']) ||
      DEFAULT_GOOGLE_DOCS_SERVICE_URL,

    // ✅ hardcoded live base url
    cloudFunctionsBaseUrl: CLOUD_FUNCTIONS_BASE_URL,

    // Optional default folder to save docs into
    defaultDocFolderId: this.readConfigValue([
      'googleDocsDefaultFolderId',
      'GOOGLE_DOCS_DEFAULT_FOLDER_ID'
    ])
  };

  async initialize(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise<void>(async (resolve, reject) => {
      try {
        if (!this.config.clientId) {
          throw new Error('Missing Google OAuth client id (oauth2ClientId).');
        }

        await this.loadScript('https://accounts.google.com/gsi/client');
        await this.loadScript('https://apis.google.com/js/api.js');

        await new Promise<void>((gapiResolve, gapiReject) => {
          if (!gapi?.load) {
            gapiReject(new Error('Google API script did not load.'));
            return;
          }
          gapi.load('client:picker', {
            callback: () => {
              this.gapiReady = true;
              this.pickerReady = true;
              gapiResolve();
            },
            onerror: () => gapiReject(new Error('Google API client failed to load.'))
          });
        });

        this.tokenClient = google.accounts.oauth2.initTokenClient({
          client_id: this.config.clientId,
          scope: this.scopes,
          callback: () => undefined
        });

        resolve();
      } catch (error) {
        reject(error);
      }
    });

    return this.initPromise;
  }

  getConfigSummary() {
    return {
      clientIdPresent: Boolean(this.config.clientId),
      googleDeveloperKeyPresent: Boolean(this.config.googleDeveloperKey),
      lambdaUrl: this.config.lambdaUrl,
      cloudFunctionsBaseUrl: this.config.cloudFunctionsBaseUrl,
      generateGeminiEndpoint: this.getGenerateGeminiEndpoint(),
      defaultDocFolderIdPresent: Boolean(this.config.defaultDocFolderId)
    };
  }

  async requestAccessToken(prompt: '' | 'consent' = ''): Promise<string> {
    await this.initialize();

    if (this.accessToken && Date.now() < this.tokenExpiryMs - 30000) {
      return this.accessToken;
    }

    const tokenResponse = await new Promise<any>((resolve, reject) => {
      if (!this.tokenClient) {
        reject(new Error('Token client is not initialized.'));
        return;
      }
      this.tokenClient.callback = (resp: any) => {
        if (resp?.error) {
          reject(new Error(resp.error));
          return;
        }
        resolve(resp);
      };
      this.tokenClient.requestAccessToken({ prompt });
    });

    this.accessToken = tokenResponse.access_token;
    this.tokenExpiryMs = Date.now() + (tokenResponse.expires_in || 0) * 1000;

    await this.ensureUserEmail();
    return this.accessToken;
  }

  // ---------------------------------------------------------------------------
  // List/Search (legacy AI+tools endpoint) — unchanged
  // ---------------------------------------------------------------------------

  async listDocs(): Promise<DriveDoc[]> {
    const token = await this.requestAccessToken('');
    await this.ensureUserEmail();

    const payload = {
      prompt:
        'List my 20 most recently modified Google Docs. Use the tools. Return ONLY a JSON array of objects with keys: id, name, modifiedTime, owner, url.',
      history: [],
      enable_google: true,
      google_access_token: token,
      length: 'brief',
      thinkingLevel: 'minimal'
    };

    const resp = await this.callLambda(payload);
    const docsFromTools = this.extractDocsFromToolCalls(resp);

    if (docsFromTools.length) return this.filterTenantDocs(docsFromTools);

    const parsed = this.tryParseJsonArray(resp.text || '');
    if (parsed.length) return this.filterTenantDocs(parsed);

    return [];
  }

  async searchDocs(query: string): Promise<DriveDoc[]> {
    const trimmed = query.trim();
    if (!trimmed) throw new Error('Add a search query to look up Google Docs.');

    const token = await this.requestAccessToken('');
    await this.ensureUserEmail();

    const payload = {
      prompt: `Search my Google Drive for Google Docs matching: "${trimmed}". Use tools. Return ONLY a JSON array of objects with keys: id, name, modifiedTime, owner, url.`,
      history: [],
      enable_google: true,
      google_access_token: token,
      length: 'brief',
      thinkingLevel: 'minimal'
    };

    const resp = await this.callLambda(payload);
    const docsFromTools = this.extractDocsFromToolCalls(resp);

    if (docsFromTools.length) return this.filterTenantDocs(docsFromTools);

    const parsed = this.tryParseJsonArray(resp.text || '');
    if (parsed.length) return this.filterTenantDocs(parsed);

    return [];
  }

  // ---------------------------------------------------------------------------
  // Create (NEW: server-side generateGemini endpoint)
  // ---------------------------------------------------------------------------

  /**
   * Creates a Google Doc by calling:
   * POST https://us-central1-clarichatai.cloudfunctions.net/generateGemini
   *
   * Contract (server):
   * Body: {
   *   assistantText: string,
   *   userText?: string,
   *   google_access_token: string (or googleAccessToken),
   *   google_doc_title?: string,
   *   google_doc_folder_id?: string
   * }
   */
  async createDoc(
    title: string,
    bodyText: string,
    userPrompt?: string,
    folderId?: string
  ): Promise<DriveDoc> {
    this.enforceRateLimit('create_doc');

    const token = await this.requestAccessToken('');
    await this.ensureUserEmail();

    const safeTitle = (title || 'ClariChat Document').trim() || 'ClariChat Document';
    const content = (bodyText || '').trim();
    const question = (userPrompt || '').trim();

    const effectiveFolderId =
      (folderId || '').trim() || (this.config.defaultDocFolderId || '').trim() || undefined;

    // EXACT fields expected by generateGemini()
    const payload: any = {
      assistantText: content || '(empty)',
      userText: question || '',
      google_access_token: token,
      googleAccessToken: token, // alias supported by server
      google_doc_title: safeTitle,
      googleDocTitle: safeTitle // alias
    };

    if (effectiveFolderId) {
      payload.google_doc_folder_id = effectiveFolderId;
      payload.googleDocFolderId = effectiveFolderId; // alias
    }

    const resp = await this.callGenerateGemini(payload);

    // If server returns refreshed google access token, keep it
    if (resp?.new_access_token) {
      this.accessToken = resp.new_access_token;
      this.tokenExpiryMs = Date.now() + 30 * 60 * 1000;
    }

    if (!resp?.success) {
      const serverMsg = resp?.error || resp?.google_doc?.error || 'Doc creation failed.';
      throw new Error(serverMsg);
    }

    const gd = resp.google_doc || {};
    const url = gd.url || '';
    if (!url) {
      throw new Error('Doc created, but no document URL was returned.');
    }

    const docId = gd.document_id || gd.documentId || this.extractDocId(url) || '';

    return {
      id: String(docId || ''),
      name: String(gd.title || safeTitle),
      url: String(url)
    };
  }

  async createDocFromAssistantResponse(
    title: string,
    assistantMarkdown: string,
    userPrompt?: string,
    folderId?: string
  ): Promise<DriveDoc> {
    const t = (title || '').trim() || 'ClariChat — Assistant Response';
    const body = (assistantMarkdown || '').trim();
    return this.createDoc(t, body, userPrompt, folderId);
  }

  /**
   * Creates a Google Doc using the Drive API (`files.create`) and Google Docs API (`documents.batchUpdate`),
   * with content as plain text. Uses the same OAuth access token as other methods (`drive.file` + `documents`).
   */
  async createDriveDocWithPlainText(
    title: string,
    plainText: string,
    folderId?: string
  ): Promise<DriveDoc> {
    this.enforceRateLimit('create_drive_doc_native');

    const token = await this.requestAccessToken('');
    await this.ensureUserEmail();

    const safeTitle = (title || 'ClariChat Document').trim() || 'ClariChat Document';
    const body = (plainText || '').trim() || '(empty)';

    const effectiveFolderId =
      (folderId || '').trim() || (this.config.defaultDocFolderId || '').trim() || undefined;

    const metadata: Record<string, unknown> = {
      name: safeTitle,
      mimeType: 'application/vnd.google-apps.document'
    };
    if (effectiveFolderId) {
      metadata.parents = [effectiveFolderId];
    }

    const createResp = await fetch(
      'https://www.googleapis.com/drive/v3/files?fields=id,name,webViewLink,mimeType',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(metadata)
      }
    );

    const createData: any = await createResp.json().catch(() => ({}));
    if (!createResp.ok) {
      const msg =
        createData?.error?.message ||
        createData?.error ||
        `Drive files.create failed (${createResp.status})`;
      throw new Error(msg);
    }

    const docId = String(createData.id || '');
    if (!docId) {
      throw new Error('Drive API did not return a document id.');
    }

    await this.docsBatchInsertPlainText(token, docId, body);

    const url =
      String(createData.webViewLink || '') ||
      `https://docs.google.com/document/d/${docId}/edit`;

    return {
      id: docId,
      name: String(createData.name || safeTitle),
      url
    };
  }

  private async docsBatchInsertPlainText(
    token: string,
    documentId: string,
    text: string
  ): Promise<void> {
    const safe = text.replace(/\r\n/g, '\n');
    const CHUNK = 4500;
    const chunks: string[] = [];
    for (let i = 0; i < safe.length; i += CHUNK) {
      chunks.push(safe.slice(i, i + CHUNK));
    }
    if (chunks.length === 0) {
      chunks.push('(empty)');
    }

    let insertIndex = 1;
    const requests = chunks.map((chunk) => {
      const req = {
        insertText: {
          location: { index: insertIndex },
          text: chunk
        }
      };
      insertIndex += chunk.length;
      return req;
    });

    const batchUrl = `https://docs.googleapis.com/v1/documents/${encodeURIComponent(
      documentId
    )}:batchUpdate`;
    const resp = await fetch(batchUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ requests })
    });

    const data: any = await resp.json().catch(() => ({}));
    if (!resp.ok) {
      const msg =
        data?.error?.message || data?.error || `Docs batchUpdate failed (${resp.status})`;
      throw new Error(msg);
    }
  }

  // ---------------------------------------------------------------------------
  // Picker / Preview
  // ---------------------------------------------------------------------------

  async openPicker(onPicked: (doc: DriveDoc) => void): Promise<void> {
    await this.initialize();

    if (!this.pickerReady) throw new Error('Google Picker is not ready.');
    if (!this.config.googleDeveloperKey) {
      throw new Error('Missing Google Developer Key for Picker (googleApiKey / GOOGLE_API_KEY).');
    }

    const token = await this.requestAccessToken('');

    const view = new google.picker.View(google.picker.ViewId.DOCS);
    view.setMimeTypes('application/vnd.google-apps.document');

    const picker = new google.picker.PickerBuilder()
      .setDeveloperKey(this.config.googleDeveloperKey)
      .setOAuthToken(token)
      .addView(view)
      .setTitle('Select a Google Doc')
      .setCallback(async (data: any) => {
        if (data.action === google.picker.Action.PICKED) {
          const picked = data.docs?.[0];
          if (picked?.id) {
            const verified = await this.verifyTenantDoc(picked.id);
            if (verified) onPicked(verified);
          }
        }
      })
      .build();

    picker.setVisible(true);
  }

  getPreviewUrl(docId: string): string {
    return `https://docs.google.com/document/d/${docId}/preview`;
  }

  // ---------------------------------------------------------------------------
  // HTTP calls
  // ---------------------------------------------------------------------------

  /**
   * Legacy AWS Lambda call (AI + tools). Kept for list/search.
   * We intentionally do NOT add Authorization headers here to avoid CORS issues.
   */
  private async callLambda(body: any): Promise<LambdaResponse> {
    const payload = { ...(body || {}) };

    if (payload.enable_google === undefined) payload.enable_google = true;
    if (!Array.isArray(payload.history)) payload.history = [];

    if (!payload.google_access_token && this.accessToken) {
      payload.google_access_token = this.accessToken;
    }
    if (!payload.googleAccessToken && payload.google_access_token) {
      payload.googleAccessToken = payload.google_access_token;
    }

    if (payload.thinkingLevel && !payload.thinking_level) {
      payload.thinking_level = payload.thinkingLevel;
    }
    if (payload.thinking_level && !payload.thinkingLevel) {
      payload.thinkingLevel = payload.thinking_level;
    }

    const resp = await fetch(this.config.lambdaUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data: any = await resp.json().catch(() => ({}));

    if (!resp.ok) {
      const msg = data?.error || `Lambda request failed (${resp.status})`;
      throw new Error(msg);
    }

    if (data?.new_access_token) {
      this.accessToken = data.new_access_token;
      this.tokenExpiryMs = Date.now() + 30 * 60 * 1000;
    }

    return data as LambdaResponse;
  }

  /**
   * NEW: calls your Cloud Function generateGemini for doc creation.
   * POST https://us-central1-clarichatai.cloudfunctions.net/generateGemini
   *
   * We include an Authorization Bearer Firebase ID token IF available (optional).
   * The server currently uses permissive CORS + does not require auth for generateGemini,
   * but this keeps you future-proof if you lock it down later.
   */
  private async callGenerateGemini(body: any): Promise<GenerateGeminiResponse> {
    const endpoint = this.getGenerateGeminiEndpoint();
    if (!endpoint) {
      throw new Error('Missing generateGemini endpoint URL.');
    }

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    const maybeFirebaseToken = await this.tryGetFirebaseIdToken();
    if (maybeFirebaseToken) {
      headers['Authorization'] = `Bearer ${maybeFirebaseToken}`;
    }

    const resp = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...(body || {}) })
    });

    const data: any = await resp.json().catch(() => ({}));

    if (!resp.ok) {
      const msg = data?.error || data?.details || `generateGemini request failed (${resp.status})`;
      throw new Error(msg);
    }

    return data as GenerateGeminiResponse;
  }

  private getGenerateGeminiEndpoint(): string {
    return this.config.cloudFunctionsBaseUrl.replace(/\/+$/, '') + '/generateGemini';
  }

  // ---------------------------------------------------------------------------
  // Tool-call parsing (legacy list/search)
  // ---------------------------------------------------------------------------

  private extractDocsFromToolCalls(resp: LambdaResponse): DriveDoc[] {
    const calls = resp?.tool_calls || [];
    const out: DriveDoc[] = [];

    for (const c of calls) {
      const name = String(c?.name || '');
      const result = c?.result;

      if (name === 'list_recent_files' && result?.success && Array.isArray(result?.files)) {
        for (const f of result.files) {
          if (!f?.id) continue;
          out.push({
            id: String(f.id),
            name: String(f.name || ''),
            modifiedTime: f.modified || f.modifiedTime,
            url: f.link || f.url
          });
        }
      }

      if (name === 'search_google_drive' && result?.success && Array.isArray(result?.files)) {
        for (const f of result.files) {
          if (!f?.id) continue;
          out.push({
            id: String(f.id),
            name: String(f.name || ''),
            modifiedTime: f.modified || f.modifiedTime,
            url: f.link || f.url
          });
        }
      }
    }

    return out.filter((d) => !!d.id && !!d.name);
  }

  private extractCreatedDocFromToolCalls(resp: LambdaResponse): any | null {
    const calls = resp?.tool_calls || [];
    for (const c of calls) {
      const name = String(c?.name || '');
      if (name !== 'create_google_document') continue;
      const r = c?.result;
      if (r?.success && (r?.url || r?.document_id || r?.documentId)) return r;
    }
    return null;
  }

  private extractDocUrlFromToolCalls(resp: LambdaResponse): string | null {
    const calls = resp?.tool_calls || [];
    for (const c of calls) {
      const r = c?.result;
      const url = r?.url || r?.link;
      if (typeof url === 'string' && url.includes('docs.google.com/document')) return url;
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // Parsing helpers
  // ---------------------------------------------------------------------------

  private tryParseJsonObject(text: string): any | null {
    const t = (text || '').trim();
    if (!t) return null;
    if (!(t.startsWith('{') && t.endsWith('}'))) return null;
    try {
      return JSON.parse(t);
    } catch {
      return null;
    }
  }

  private tryParseJsonArray(text: string): DriveDoc[] {
    const t = (text || '').trim();
    if (!t) return [];
    if (!(t.startsWith('[') && t.endsWith(']'))) return [];
    try {
      const parsed = JSON.parse(t);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((x: any) => ({
          id: String(x?.id || ''),
          name: String(x?.name || ''),
          modifiedTime: x?.modifiedTime || x?.modified,
          owner: x?.owner,
          url: x?.url || x?.link
        }))
        .filter((d: DriveDoc) => !!d.id && !!d.name);
    } catch {
      return [];
    }
  }

  private extractDocUrl(text: string): string | null {
    const t = (text || '').trim();
    if (!t) return null;
    const m = t.match(/https:\/\/docs\.google\.com\/document\/d\/[a-zA-Z0-9_-]+\/edit[^\s]*/);
    if (m?.[0]) return m[0];
    const m2 = t.match(/https:\/\/docs\.google\.com\/document\/d\/[a-zA-Z0-9_-]+\/[^\s]*/);
    return m2?.[0] || null;
  }

  private extractDocId(url: string): string | null {
    const m = String(url || '').match(/\/document\/d\/([a-zA-Z0-9_-]+)/);
    return m?.[1] || null;
  }

  // ---------------------------------------------------------------------------
  // Rate limiting
  // ---------------------------------------------------------------------------

  private enforceRateLimit(_action: string) {
    const now = Date.now();
    if (now - this.lastCreateAt < this.createCooldownMs) {
      throw new Error('Please wait a moment before creating another doc.');
    }
    this.lastCreateAt = now;
  }

  // ---------------------------------------------------------------------------
  // Tenant verification
  // ---------------------------------------------------------------------------

  private async verifyTenantDoc(docId: string): Promise<DriveDoc | null> {
    const token = await this.requestAccessToken('');

    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${docId}?fields=id,name,modifiedTime,owners(displayName,emailAddress),webViewLink`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!response.ok) throw new Error('Unable to verify Google Doc ownership.');

    const file = await response.json();
    if (!this.isTenantOwner(file)) throw new Error('That document is outside your tenant ownership.');

    return {
      id: file.id,
      name: file.name,
      modifiedTime: file.modifiedTime,
      owner: file.owners?.[0]?.displayName || file.owners?.[0]?.emailAddress,
      url: file.webViewLink
    };
  }

  private filterTenantDocs(files: Array<DriveDoc>): Array<DriveDoc> {
    return files.filter((file) => this.isTenantOwner({ owners: [{ emailAddress: file.owner }] }));
  }

  private isTenantOwner(file: any): boolean {
    if (!this.userEmail) return true;
    const owners = file?.owners || [];
    return owners.some((owner: any) => owner.emailAddress === this.userEmail);
  }

  private async ensureUserEmail(): Promise<void> {
    if (this.userEmail) return;
    if (!this.accessToken) return;

    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${this.accessToken}` }
    });

    if (response.ok) {
      const data = await response.json();
      this.userEmail = data?.email;
    }
  }

  // ---------------------------------------------------------------------------
  // Optional Firebase auth header support (no hard dependency in your app logic)
  // ---------------------------------------------------------------------------

  private async tryGetFirebaseIdToken(): Promise<string | null> {
    try {
      const mod = await import('firebase/auth');
      const auth = mod.getAuth?.();
      const user = auth?.currentUser;
      if (!user?.getIdToken) return null;
      return await user.getIdToken();
    } catch {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Env helpers
  // ---------------------------------------------------------------------------

  private readConfigValue(keys: string[]): string {
    const envValue = keys
      .map((key) => (environment as any)[key])
      .find((value) => typeof value === 'string' && value.length > 0) as string | undefined;

    if (envValue) return envValue;

    const globalEnv = (globalThis as any)?.env || (globalThis as any)?.__env || {};
    const globalValue = keys
      .map((key) => globalEnv[key])
      .find((value) => typeof value === 'string' && value.length > 0) as string | undefined;

    return globalValue || '';
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }
}
